import { handleUnauthorized, isTokenValid } from './ssoAuth';

const BASE_URL = '/api/v1';

// Auth credentials for protected dashboard endpoints
const getHeaders = () => {
  const token = localStorage.getItem("accessToken");
  if (token && !isTokenValid(token)) {
    handleUnauthorized();
    return {};
  }
  const authHeader = token ? `Bearer ${token}` : ('Basic ' + btoa('admin:password'));
  return {
    'Authorization': authHeader,
    'Accept': 'application/json',
  };
};

const handleResponseStatus = (res) => {
  if (res.status === 401 || res.status === 403) {
    handleUnauthorized();
    throw new Error('Unauthorized');
  }
  return res;
};

// Check if backend is alive
export async function checkBackendHealth() {
  try {
    const res = await fetch(`${BASE_URL}/metrics/sessions/summary`, {
      headers: getHeaders(),
      signal: AbortSignal.timeout(2000),
    });
    handleResponseStatus(res);
    return res.ok;
  } catch {
    return false;
  }
}

// 1. Session Summary
export async function fetchSessionSummary({ from, to, mno = 'ALL', route = 'ALL', forceMock = false } = {}) {
  if (!forceMock) {
    try {
      const params = new URLSearchParams({ mno, route });
      if (from) params.append('from', from);
      if (to) params.append('to', to);

      const res = await fetch(`${BASE_URL}/metrics/sessions/summary?${params}`, { headers: getHeaders() });
      handleResponseStatus(res);
      if (res.ok) {
        return await res.json();
      }
    } catch (e) {
      if (e.message === 'Unauthorized') throw e;
      console.warn('Backend unavailable, falling back to mock summary', e);
    }
  }

  // Realistic Mock Fallback
  return {
    mno: mno || 'ALL',
    totalSessionsInitiated: 148290,
    totalSessionsCompleted: 125180,
    totalSessionsAbandoned: 18410,
    totalSessionsTimedOut: 4700,
    completionRate: 84.4,
    abandonmentRate: 12.4,
    timeoutRate: 3.2,
    currentConcurrentSessions: 38,
    peakConcurrentSessions: 142,
    peakConcurrencyTimestamp: new Date(Date.now() - 3600000 * 4).toISOString(),
    avgSessionDurationSeconds: 7.8,
    avgMenuHopsPerSession: 3.4,
    totalSessionsByMno: {
      'AIRTEL': 84200,
      'MTN': 48900,
      'VODACOM_ZA': 15190,
    },
    currentConcurrencyByMno: {
      'AIRTEL': 22,
      'MTN': 12,
      'VODACOM_ZA': 4,
    },
    topDropOffPoints: [
      { screenPrompt: 'Please enter your 4-digit PIN:', dropOffCount: 9450, dropOffRate: 51.3 },
      { screenPrompt: 'Enter recipient phone number:', dropOffCount: 4210, dropOffRate: 22.8 },
      { screenPrompt: 'Enter amount to transfer:', dropOffCount: 2980, dropOffRate: 16.2 },
      { screenPrompt: 'Select Account (1. Current, 2. Savings):', dropOffCount: 1770, dropOffRate: 9.7 },
    ]
  };
}

// 2. Concurrency Time-series
export async function fetchConcurrencyTimeline({ from, to, mno = 'ALL', route = 'ALL', forceMock = false } = {}) {
  if (!forceMock) {
    try {
      const params = new URLSearchParams({ mno, route });
      if (from) params.append('from', from);
      if (to) params.append('to', to);

      const res = await fetch(`${BASE_URL}/metrics/sessions/concurrency?${params}`, { headers: getHeaders() });
      handleResponseStatus(res);
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0) return data;
      }
    } catch (e) {
      if (e.message === 'Unauthorized') throw e;
      console.warn('Backend unavailable, falling back to mock timeline', e);
    }
  }

  // Generate 24-hour timeline points
  const points = [];
  const now = Date.now();
  for (let i = 24; i >= 0; i--) {
    const time = new Date(now - i * 3600000);
    const hour = time.getHours();
    // Simulate natural banking peak hours (10:00 - 16:00)
    const multiplier = (hour >= 9 && hour <= 17) ? 1.8 : 0.6;
    const base = Math.floor((20 + Math.sin(hour / 3) * 15) * multiplier);

    points.push({
      timestamp: time.toISOString(),
      activeSessions: Math.max(5, base + Math.floor(Math.random() * 8)),
      peakSessions: Math.max(8, base + Math.floor(Math.random() * 20) + 10),
      completedInWindow: Math.floor(base * 12 + Math.random() * 50),
      errorInWindow: Math.floor(base * 0.8 + Math.random() * 4),
    });
  }
  return points;
}

// 3. Flow Funnel
export async function fetchFlowFunnel(flowName = 'BALANCE_ENQUIRY', { from, to, mno = 'ALL', route = 'ALL', forceMock = false } = {}) {
  if (!forceMock) {
    try {
      const params = new URLSearchParams({ flow: flowName, flowName, mno, route });
      if (from) params.append('from', from);
      if (to) params.append('to', to);

      const res = await fetch(`${BASE_URL}/metrics/flows/funnel?${params}`, { headers: getHeaders() });
      handleResponseStatus(res);
      if (res.ok) {
        const data = await res.json();
        if (data && data.steps && data.steps.length > 0) return data;
      }
    } catch (e) {
      if (e.message === 'Unauthorized') throw e;
      console.warn('Backend unavailable, falling back to mock funnel', e);
    }
  }

  // Funnel templates
  if (flowName === 'FUNDS_TRANSFER') {
    return {
      flowName: 'Funds Transfer',
      totalSessionsStarted: 34200,
      totalSessionsCompleted: 24960,
      overallConversionRate: 73.0,
      steps: [
        { stepNumber: 1, stepName: 'MAIN_MENU', reachedCount: 34200, conversionRate: 100.0, dropOffCount: 2200, dropOffRate: 6.4 },
        { stepNumber: 2, stepName: 'RECIPIENT_NUMBER', reachedCount: 32000, conversionRate: 93.6, dropOffCount: 3800, dropOffRate: 11.9 },
        { stepNumber: 3, stepName: 'ENTER_AMOUNT', reachedCount: 28200, conversionRate: 82.5, dropOffCount: 1900, dropOffRate: 6.7 },
        { stepNumber: 4, stepName: 'PIN_ENTRY', reachedCount: 26300, conversionRate: 76.9, dropOffCount: 1340, dropOffRate: 5.1 },
        { stepNumber: 5, stepName: 'CONFIRMATION', reachedCount: 24960, conversionRate: 73.0, dropOffCount: 0, dropOffRate: 0.0 },
      ]
    };
  }

  if (flowName === 'MINI_STATEMENT') {
    return {
      flowName: 'Mini Statement',
      totalSessionsStarted: 28900,
      totalSessionsCompleted: 25430,
      overallConversionRate: 88.0,
      steps: [
        { stepNumber: 1, stepName: 'MAIN_MENU', reachedCount: 28900, conversionRate: 100.0, dropOffCount: 1200, dropOffRate: 4.2 },
        { stepNumber: 2, stepName: 'PIN_ENTRY', reachedCount: 27700, conversionRate: 95.8, dropOffCount: 2270, dropOffRate: 8.2 },
        { stepNumber: 3, stepName: 'STATEMENT_DISPLAY', reachedCount: 25430, conversionRate: 88.0, dropOffCount: 0, dropOffRate: 0.0 },
      ]
    };
  }

  // Default: Balance Enquiry
  return {
    flowName: 'Balance Enquiry',
    totalSessionsStarted: 62400,
    totalSessionsCompleted: 56160,
    overallConversionRate: 90.0,
    steps: [
      { stepNumber: 1, stepName: 'MAIN_MENU', reachedCount: 62400, conversionRate: 100.0, dropOffCount: 1800, dropOffRate: 2.9 },
      { stepNumber: 2, stepName: 'PIN_ENTRY', reachedCount: 60600, conversionRate: 97.1, dropOffCount: 4440, dropOffRate: 7.3 },
      { stepNumber: 3, stepName: 'ACCOUNT_DISPLAY', reachedCount: 56160, conversionRate: 90.0, dropOffCount: 0, dropOffRate: 0.0 },
    ]
  };
}

// 4. Menu Options Usage
export async function fetchMenuOptionUsage({ from, to, mno = 'ALL', route = 'ALL', forceMock = false } = {}) {
  if (!forceMock) {
    try {
      const params = new URLSearchParams({ mno, route });
      if (from) params.append('from', from);
      if (to) params.append('to', to);

      const res = await fetch(`${BASE_URL}/metrics/flows/menu-options?${params}`, { headers: getHeaders() });
      handleResponseStatus(res);
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0) {
          return data.map(item => ({
            menuState: item.menuState || 'MAIN_MENU',
            selectedOption: item.optionSelected || item.selectedOption || 'Menu Option',
            optionSelected: item.optionSelected || item.selectedOption || 'Menu Option',
            count: item.selectionCount ?? item.count ?? 0,
            selectionCount: item.selectionCount ?? item.count ?? 0,
            percentage: item.percentage || 0,
          }));
        }
      }
    } catch (e) {
      if (e.message === 'Unauthorized') throw e;
      console.warn('Backend unavailable, falling back to mock menu options', e);
    }
  }

  return [
    { menuState: 'MAIN_MENU', selectedOption: '1. Balance Inquiry', optionSelected: '1. Balance Inquiry', count: 62400, selectionCount: 62400, percentage: 54.2 },
    { menuState: 'MAIN_MENU', selectedOption: '2. Mini Statement', optionSelected: '2. Mini Statement', count: 28900, selectionCount: 28900, percentage: 25.1 },
    { menuState: 'MAIN_MENU', selectedOption: '3. Funds Transfer', optionSelected: '3. Funds Transfer', count: 18200, selectionCount: 18200, percentage: 15.8 },
    { menuState: 'MAIN_MENU', selectedOption: '4. Buy Airtime', optionSelected: '4. Buy Airtime', count: 5600, selectionCount: 5600, percentage: 4.9 },
  ];
}

// 5. Menu Error Rates
export async function fetchMenuErrorRates({ from, to, mno = 'ALL', route = 'ALL', forceMock = false } = {}) {
  if (!forceMock) {
    try {
      const params = new URLSearchParams({ mno, route });
      if (from) params.append('from', from);
      if (to) params.append('to', to);

      const res = await fetch(`${BASE_URL}/metrics/flows/menu-errors?${params}`, { headers: getHeaders() });
      handleResponseStatus(res);
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0) return data;
      }
    } catch (e) {
      if (e.message === 'Unauthorized') throw e;
      console.warn('Backend unavailable, falling back to mock menu errors', e);
    }
  }

  return [
    {
      menuState: 'PIN_ENTRY',
      totalHops: 60600,
      errorHops: 4440,
      errorRate: 7.3,
      topErrorReasons: ['INVALID_PIN (3,120)', 'USER_TIMEOUT (980)', 'ATTEMPTS_EXCEEDED (340)'],
    },
    {
      menuState: 'RECIPIENT_NUMBER',
      totalHops: 32000,
      errorHops: 3800,
      errorRate: 11.9,
      topErrorReasons: ['INVALID_MSISDN_FORMAT (2,400)', 'UNREGISTERED_WALLET (1,100)', 'NETWORK_TIMEOUT (300)'],
    },
    {
      menuState: 'ENTER_AMOUNT',
      totalHops: 28200,
      errorHops: 1900,
      errorRate: 6.7,
      topErrorReasons: ['EXCEEDS_DAILY_LIMIT (1,250)', 'INSUFFICIENT_FUNDS (450)', 'BELOW_MINIMUM (200)'],
    },
  ];
}

// 6. Live USSD Dialogue Dispatcher (For Interactive Sandbox)
export async function sendLiveUssdRequest({ mno = 'airtel', sessionId, msisdn, newRequest, input }) {
  const xmlPayload = `<request>
  <sessionId>${sessionId}</sessionId>
  <msisdn>${msisdn}</msisdn>
  <newRequest>${newRequest}</newRequest>
  <input>${input}</input>
  <type>pull</type>
</request>`;

  const res = await fetch(`${BASE_URL}/transport/ussd/dynamic/${mno.toLowerCase()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/xml',
      'Accept': 'application/xml, text/xml, */*',
    },
    body: xmlPayload,
  });

  const text = await res.text();
  return { ok: res.ok, status: res.status, rawXml: text };
}

// 7. MNO Gateway Configurations
export async function fetchMnoConfigs(forceMock = false) {
  if (!forceMock) {
    try {
      const res = await fetch(`${BASE_URL}/metrics/mno-configs`, { headers: getHeaders() });
      handleResponseStatus(res);
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0) return data;
      }
    } catch (e) {
      if (e.message === 'Unauthorized') throw e;
      console.warn('Backend unavailable, falling back to mock MNO configs', e);
    }
  }

  return [
    {
      code: 'AIRTEL',
      displayName: 'Airtel Uganda',
      protocol: 'XML_REST',
      enabled: true,
      fieldMappings: {
        msisdnField: 'msisdn',
        userInputFields: ['subscriberInput', 'input'],
        sessionIdField: 'sessionId',
        transactionIdField: 'transactionId',
        newRequestField: 'newRequest',
      },
      shortcodeRoutes: [
        { shortcode: '*185#', targetRoute: 'HELLO_MONEY' },
        { shortcode: '*185*9#', targetRoute: 'DIGITAL_WALLET' },
      ],
      stateRules: {
        continuationState: 'FC',
        terminalState: 'FB',
      }
    },
    {
      code: 'MTN',
      displayName: 'MTN Uganda',
      protocol: 'XML_RPC',
      enabled: true,
      fieldMappings: {
        msisdnField: 'MSISDN',
        userInputFields: ['USERDATA', 'USSDRequestString', 'input'],
        sessionIdField: 'SESSIONID',
        transactionIdField: 'TRANSACTIONID',
        newRequestField: 'NEWREQUEST',
      },
      shortcodeRoutes: [
        { shortcode: '*165#', targetRoute: 'HELLO_MONEY' },
        { shortcode: '*233*190#', targetRoute: 'USSD_WRAPPER' },
      ],
      stateRules: {
        continuationState: 'FC',
        terminalState: 'FB',
      }
    },
    {
      code: 'VODACOM_ZA',
      displayName: 'Vodacom South Africa',
      protocol: 'JSON_REST',
      enabled: true,
      fieldMappings: {
        msisdnField: 'msisdn',
        userInputFields: ['input', 'userData'],
        sessionIdField: 'sessionId',
        transactionIdField: 'transactionId',
        newRequestField: 'newRequest',
      },
      shortcodeRoutes: [
        { shortcode: '*150*00#', targetRoute: 'HELLO_MONEY' },
        { shortcode: '*150*01#', targetRoute: 'DIGITAL_WALLET' },
      ],
      stateRules: {
        continuationState: 'FC',
        terminalState: 'FB',
      }
    }
  ];
}

export async function saveMnoConfig(config) {
  try {
    const res = await fetch(`${BASE_URL}/metrics/mno-configs`, {
      method: 'POST',
      headers: {
        ...getHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(config),
    });
    handleResponseStatus(res);
    if (res.ok) {
      return await res.json();
    }
    throw new Error(`Failed to save MNO config (${res.status})`);
  } catch (e) {
    if (e.message === 'Unauthorized') throw e;
    console.error('Save MNO error:', e);
    throw e;
  }
}

export async function deleteMnoConfig(id) {
  try {
    const res = await fetch(`${BASE_URL}/metrics/mno-configs/${id}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
    handleResponseStatus(res);
    return res.ok;
  } catch (e) {
    if (e.message === 'Unauthorized') throw e;
    console.error('Delete MNO error:', e);
    throw e;
  }
}

